import { createTheme } from '@mui/material/styles';

// ============================================================
// DESIGN TOKENS — SKY & AMBER PALETTE
// ============================================================
// Primary: Deep sky blue    #0077B6 (AA contrast on white)
// Primary light: #48CAE4   — backgrounds, hover states
// Primary lighter: #E0F4FD — subtle fills
// Accent: Warm amber        #F5A623 (transport/logistics energy)
// Accent light: #FFF3CD    — highlights
// Text primary: #0D1B2A    — near-black for max readability
// Text secondary: #4A5568  — muted text
// Background: #F7FBFF      — off-white with blue tint
// Surface: #FFFFFF
// ============================================================

export const DESIGN_TOKENS = {
  colors: {
    primaryDark: '#005F8E',
    primary: '#0077B6',
    primaryLight: '#48CAE4',
    primaryLighter: '#E0F4FD',
    accentDark: '#D4890A',
    accent: '#F5A623',
    accentLight: '#FFF3CD',
    textPrimary: '#0D1B2A',
    textSecondary: '#4A5568',
    textDisabled: '#9CA3AF',
    slate: '#475569',
    background: '#F7FBFF',
    surface: '#FFFFFF',
    border: '#CAE7F7',
    error: '#D32F2F',
    success: '#2E7D32',
    warning: '#ED6C02',
    info: '#0288D1',
    verified: '#1B5E20',
    verifiedBackground: '#E8F5E9',
    highlighted: '#B45309',
    highlightedBackground: '#FFFBEB',
    errorLight: 'red',
  },
  typography: {
    fontFamilyDisplay: '"DM Sans", "Helvetica Neue", Arial, sans-serif',
    fontFamilyBody: '"Inter", "Roboto", "Helvetica", Arial, sans-serif',
  },
  spacing: {
    sectionPadding: '80px 0',
  },
  shadows: {
    card: '0 2px 12px rgba(0, 119, 182, 0.08)',
    cardHover: '0 6px 24px rgba(0, 119, 182, 0.16)',
    elevated: '0 8px 32px rgba(0, 0, 0, 0.10)',
  },
  borderRadius: {
    card: '12px',
    button: '8px',
    chip: '16px',
  },
};

// ============================================================
// MUI THEME
// ============================================================

const transportTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: DESIGN_TOKENS.colors.primary,
      light: DESIGN_TOKENS.colors.primaryLight,
      dark: DESIGN_TOKENS.colors.primaryDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: DESIGN_TOKENS.colors.accent,
      light: DESIGN_TOKENS.colors.accentLight,
      dark: DESIGN_TOKENS.colors.accentDark,
      contrastText: '#0D1B2A',
    },
    error: { main: DESIGN_TOKENS.colors.error },
    warning: { main: DESIGN_TOKENS.colors.warning },
    success: { main: DESIGN_TOKENS.colors.success },
    info: { main: DESIGN_TOKENS.colors.info },
    text: {
      primary: DESIGN_TOKENS.colors.textPrimary,
      secondary: DESIGN_TOKENS.colors.textSecondary,
      disabled: DESIGN_TOKENS.colors.textDisabled,
    },
    background: {
      default: DESIGN_TOKENS.colors.background,
      paper: DESIGN_TOKENS.colors.surface,
    },
    divider: DESIGN_TOKENS.colors.border,
  },
  typography: {
    fontFamily: DESIGN_TOKENS.typography.fontFamilyBody,
    h1: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 700,
      fontSize: '3rem',
      letterSpacing: '-0.02em',
      color: DESIGN_TOKENS.colors.textPrimary,
    },
    h2: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.015em',
      color: DESIGN_TOKENS.colors.textPrimary,
    },
    h3: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 600,
      fontSize: '1.375rem',
    },
    h5: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    h6: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 600,
      fontSize: '1rem',
    },
    subtitle1: {
      fontSize: '1.0625rem',
      lineHeight: 1.6,
      color: DESIGN_TOKENS.colors.textSecondary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.65,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: DESIGN_TOKENS.colors.textSecondary,
    },
    button: {
      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
      fontWeight: 600,
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.button,
          padding: '10px 24px',
          fontSize: '0.9375rem',
          transition: 'all 0.2s ease',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: DESIGN_TOKENS.shadows.cardHover,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.card,
          boxShadow: DESIGN_TOKENS.shadows.card,
          border: `1px solid ${DESIGN_TOKENS.colors.border}`,
          transition: 'box-shadow 0.25s ease, transform 0.25s ease',
          '&:hover': {
            boxShadow: DESIGN_TOKENS.shadows.cardHover,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.chip,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: DESIGN_TOKENS.borderRadius.button,
            backgroundColor: DESIGN_TOKENS.colors.surface,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: `${DESIGN_TOKENS.borderRadius.card} !important`,
          border: `1px solid ${DESIGN_TOKENS.colors.border}`,
          boxShadow: 'none',
          marginBottom: '8px',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            boxShadow: DESIGN_TOKENS.shadows.card,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: DESIGN_TOKENS.colors.surface,
          color: DESIGN_TOKENS.colors.textPrimary,
          boxShadow: `0 1px 0 ${DESIGN_TOKENS.colors.border}`,
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
        
        html {
          scroll-behavior: smooth;
        }
        
        body {
          background-color: ${DESIGN_TOKENS.colors.background};
        }

        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: ${DESIGN_TOKENS.colors.primaryLighter};
        }
        ::-webkit-scrollbar-thumb {
          background: ${DESIGN_TOKENS.colors.primaryLight};
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: ${DESIGN_TOKENS.colors.primary};
        }

        a {
          color: ${DESIGN_TOKENS.colors.primary};
          text-decoration: none;
        }
        a:hover {
          text-decoration: underline;
        }

        *:focus-visible {
          outline: 3px solid ${DESIGN_TOKENS.colors.accent};
          outline-offset: 2px;
        }
      `,
    },
  },
});

export default transportTheme;
